import { useState, useEffect } from "react";
import clsx from "clsx";
import { compareAsc, addDays } from "date-fns";

import {
  DAYS_UPLOAD_LATER_BANNER,
  getUploadLaterDocsTitle,
} from "@utils/index";
import { AssetModel } from "@interfaces/index";
import { localStorageService } from "@services/index";
import { useUserStore } from "@stores/index";
import { AlertTriangleSvg, AssetItem, XSvg } from "@components/index";

export const UploadLaterBannerTransactions = ({
  assets,
  className,
}: {
  assets: AssetModel[];
  className?: string;
}) => {
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const [isHidden, setIsHidden] = useState(true);

  useEffect(() => {
    const value = localStorageService.getUploadLaterBannerDate();
    const lastDate = new Date(value || "");
    if (
      !value ||
      compareAsc(new Date(), addDays(lastDate, DAYS_UPLOAD_LATER_BANNER)) > 0
    ) {
      setIsHidden(false);
    }
  }, []);

  const filteredAssets = assets.filter(
    (asset) => asset.uploadLaterAgreement || asset.uploadLaterInvoice,
  );
  const assetNames = filteredAssets.map(({ name }) => name).join(", ");

  const onClose = () => {
    setIsHidden(true);
    localStorageService.setUploadLaterBannerDate();
  };

  return !disabledFeatures.includes("Assets") && !isHidden && assetNames ? (
    <div
      className={clsx(
        "relative my-4 rounded-lg border border-yellow-40 bg-yellow-200 p-4 md:mb-3 md:mt-5 md:flex md:items-center md:gap-2 md:py-3.5 md:pl-4 md:pr-12",
        className,
      )}
    >
      <div className="flex items-center gap-1 font-bold text-black-100 *:size-6 md:hidden">
        <AlertTriangleSvg />
        Assets with pending document uploads
        <button type="button" className="ml-10 self-start" onClick={onClose}>
          <XSvg />
        </button>
      </div>
      <div className="hidden *:size-5 md:inline-block md:*:size-6">
        <AlertTriangleSvg />
      </div>
      <div className="mt-3 md:hidden">
        {filteredAssets.map((asset) => (
          <div
            key={asset.uniqueId}
            className="mb-1 flex items-center gap-2 border-b border-b-black-24 pb-1 last:mb-0 last:border-0 last:pb-0"
          >
            <div className="*:text-black-54">
              <AssetItem type={asset.type} justTypeIcon />
            </div>
            <div>
              <div className="text-black-100">{asset.name}</div>
              <div className="text-100 text-black-54">
                {getUploadLaterDocsTitle(asset)}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="hidden text-black-100 *:font-bold md:block">
        <span>Complete document uploads</span> for assets{" "}
        <span>{assetNames} to avoid issues</span> during tax period submission.
      </div>
      <button
        type="button"
        className="absolute right-4 ml-10 hidden md:inline"
        onClick={onClose}
      >
        <XSvg />
      </button>
    </div>
  ) : null;
};
